import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import FormDrawerWrapper from '../../../../features/components/FormDrawerWrapper'
import EditSectionsList from '../../../../features/components/Forms/EditForm/EditSectionsList'
import NameSectionPreview from '../../../ReusableFormSections/NameSection/NameSectionPreview'
import NameSectionForm from './Sections/NameSection'
import AudienceSectionForm from './Sections/AudienceSection/AudienceSectionForm'
import AudienceSectionPreview from './Sections/AudienceSection/AudienceSectionPreview'
import LanguagesSectionPreview from './Sections/LanguagesSection/LanguagesSectionPreview'
import LanguagesSectionForm from './Sections/LanguagesSection/LanguagesSectionForm'
import GeolocationSectionPreview from './Sections/GeolocationSection/GeolocationSectionPreview'
import GeolocationSectionForm from './Sections/GeolocationSection/GeolocationSectionForm'
import GenderSectionPreview from './Sections/GenderSection/GenderSecrionPreview'
import GenderSectionForm from './Sections/GenderSection/GenderSectionForm'
import AgesSectionPreview from './Sections/AgesSection/AgesSectionPreview'
import AgesSectionForm from './Sections/AgesSection/AgesSectionForm'
import DatesSectionPreview from './Sections/DatesSection/DatesSectionPreview'
import DatesSectionForm from './Sections/DatesSection/DatesSectionForm'
import PixelsSectionPreview from './Sections/PixelSection/PixelsSectionPreview'
import PixelsSectionForm from './Sections/PixelSection/PixelsSectionForm'
import CategoriesAndKeywordsSectionForm from './Sections/CategoriesAndKeywordsSection/CategoriesAndKeywordsSectionForm'
import CategoriesAndKeywordsSectionPreview from './Sections/CategoriesAndKeywordsSection/CategoriesAndKeywordsSectionPreview'
import UrlsAndAppsSectionForm from './Sections/UrlsAndAppsSection/UrlsAndAppsSectionForm'
import UrlsAndAppsSectionPreview from './Sections/UrlsAndAppsSection/UrlsAndAppsSectionPreview'
import ExchangesSectionForm from './Sections/ExchangesSection/ExchangesSectionForm'
import ExchangesSectionPreview from './Sections/ExchangesSection/ExchangesSectionPreview'
import ThirdPartyVerificationSectionForm from './Sections/ThirdPartyVerificationSection/ThirdPartyVerificationSectionForm'
import ThirdPartyVerificationSectionPreview from './Sections/ThirdPartyVerificationSection/ThirdPartyVerificationSectionPreview'
import PositionsSectionForm from './Sections/PositionsSection/PositionsSectionForm'
import PositionsSectionPreview from './Sections/PositionsSection/PositionsSectionPreview'
import ViewabilitySectionFrom from './Sections/ViewabilitySection/ViewabilitySectionFrom'
import ViewabilitySectionPreview from './Sections/ViewabilitySection/ViewabilitySectionPreview'

import useManageDrawer from '../../../../hooks/formHooks/useManageDrawer'
import useManageEditFormData from '../../../../hooks/formHooks/useManageEditFormData'
import { useLoadAdditionalData } from './useLoadAdditionalData'
import { getIsPixelRequired } from '../../lineItemHelpers'
import { insertIf } from '../../../../helpers/common'

import { lineItemSelector, lineItemWasLoadedSelector } from '../../../../modules/selectors/lineItems'
import { campaignSelector } from '../../../../modules/selectors/campaigns'
import { selectedDV360AdAccountSelector } from '../../../../modules/selectors/adAccounts'
import { getLineItem, clearLineItem } from '../../../../modules/actions/lineItems'
import { clearDv360Targeting } from '../../../../modules/actions/dv360Constants'

import { LINE_ITEM_DV360_EDIT } from '../../../../constants/forms'
import { COUNTRY_OR_REGION } from './Sections/GeolocationSection/fields'
import { CUSTOM_AUDIENCES } from '../../../ReusableFormFields/Audiences/AudiencesFields/fields'
import { DV_360_PLATFORM } from '../../../../constants/selectLists/platformList'
import { LANGUAGES } from '../../../ReusableFormFields/LanguagesFields/fields'
import { GENDER } from '../../../ReusableFormFields/GenderFields/fields'
import { NAME } from '../LineItemDv360Create/LineItemDv360CreateForm/LineItemDV360CreateMainForm/fields'
import {
  LINE_ITEM_TYPE,
  VIDEO_DEFAULT
} from '../../UploadDV360AdCreative/UploadDV360AdCreativeForm/LineItemForm/fields'

function LineItemDV360Edit({ shouldBeCleared }) {
  const dispatch = useDispatch()

  const campaign = useSelector(campaignSelector)
  const lineItem = useSelector(lineItemSelector)
  const itemWasLoaded = useSelector(lineItemWasLoadedSelector)

  console.log('lineItem', lineItem)

  const lineItemType = lineItem[LINE_ITEM_TYPE]

  const { id: selectedAdAccountId } = useSelector(selectedDV360AdAccountSelector)

  const isPixelRequired = useMemo(() => {
    return getIsPixelRequired(campaign)
  }, [campaign])

  const { isFormOpen } = useManageDrawer({
    formName: LINE_ITEM_DV360_EDIT
  })

  const isAdditionalDataLoading = useLoadAdditionalData({ isFormOpen })

  const isInitialDataLoading = !itemWasLoaded || isAdditionalDataLoading

  const getItemDataHandler = useCallback(
    selectedEditItemId => {
      dispatch(getLineItem({ id: selectedEditItemId, account: selectedAdAccountId }, DV_360_PLATFORM))
    },
    [dispatch, selectedAdAccountId]
  )

  const clearItemDataHandler = useCallback(() => {
    if (shouldBeCleared) {
      dispatch(clearLineItem())
    }

    dispatch(clearDv360Targeting())
  }, [dispatch, shouldBeCleared])

  useManageEditFormData({
    formName: LINE_ITEM_DV360_EDIT,
    loadedDataId: lineItem.id,
    getDataHandler: getItemDataHandler,
    clearDataHandler: clearItemDataHandler
  })

  const editSections = useMemo(
    () => [
      {
        sectionName: NAME,
        title: 'Name',
        PreviewComponent: <NameSectionPreview dataSelector={lineItemSelector} />,
        FormComponent: <NameSectionForm />
      },
      {
        sectionName: 'dates',
        title: 'Dates',
        PreviewComponent: <DatesSectionPreview lineItem={lineItem} />,
        FormComponent: <DatesSectionForm />
      },
      ...insertIf(isPixelRequired, {
        sectionName: 'pixels',
        title: 'Pixels',
        PreviewComponent: <PixelsSectionPreview lineItem={lineItem} />,
        FormComponent: <PixelsSectionForm />
      }),
      {
        sectionName: CUSTOM_AUDIENCES,
        title: 'Custom Audience',
        PreviewComponent: <AudienceSectionPreview />,
        FormComponent: <AudienceSectionForm />
      },
      {
        sectionName: LANGUAGES,
        title: 'Languages',
        PreviewComponent: <LanguagesSectionPreview />,
        FormComponent: <LanguagesSectionForm />
      },
      {
        sectionName: COUNTRY_OR_REGION,
        title: 'Location Targeting',
        PreviewComponent: <GeolocationSectionPreview />,
        FormComponent: <GeolocationSectionForm />
      },
      {
        sectionName: GENDER,
        title: 'Gender',
        PreviewComponent: <GenderSectionPreview />,
        FormComponent: <GenderSectionForm />
      },
      {
        sectionName: 'Age',
        title: 'Age',
        PreviewComponent: <AgesSectionPreview />,
        FormComponent: <AgesSectionForm />
      },
      {
        sectionName: 'urls_and_apps',
        title: 'URLs and Apps',
        PreviewComponent: <UrlsAndAppsSectionPreview />,
        FormComponent: <UrlsAndAppsSectionForm />
      },
      {
        sectionName: 'categories_and_keywords',
        title: 'Categories and Keywords',
        PreviewComponent: <CategoriesAndKeywordsSectionPreview />,
        FormComponent: <CategoriesAndKeywordsSectionForm />
      },
      {
        sectionName: 'third_party_verification',
        title: 'IAS brand safety targeting',
        PreviewComponent: <ThirdPartyVerificationSectionPreview />,
        FormComponent: <ThirdPartyVerificationSectionForm />
      },
      {
        sectionName: 'viewability',
        title: 'Viewability',
        PreviewComponent: <ViewabilitySectionPreview />,
        FormComponent: <ViewabilitySectionFrom />
      },
      ...insertIf(lineItemType === VIDEO_DEFAULT, {
        sectionName: 'positions',
        title: 'Positions',
        PreviewComponent: <PositionsSectionPreview />,
        FormComponent: <PositionsSectionForm />
      }),
      {
        sectionName: 'exchanges',
        title: 'Exchanges',
        PreviewComponent: <ExchangesSectionPreview />,
        FormComponent: <ExchangesSectionForm />
      }
    ],
    [lineItem, isPixelRequired, lineItemType]
  )

  return (
    <FormDrawerWrapper
      title="Edit the settings of your line item"
      formName={LINE_ITEM_DV360_EDIT}
      showOpenButton={false}
    >
      <EditSectionsList editSections={editSections} isEditDataLoading={isInitialDataLoading} editItemData={lineItem} />
    </FormDrawerWrapper>
  )
}

export default LineItemDV360Edit
